import { useCallback, useEffect, useMemo, useState } from 'react'
import { taskTrackerApi } from '../../../../../api/taskTracker.api'
import { TaskTrackerTaskHistoryEvent } from '../../../../../store/taskTracker.types'
import { TaskTrackerAccessibleUser } from '../../../../../lib/taskTrackerBoardAccessibleUsers.utils'
import {
  buildTaskHistoryPerformerNamesFromAccessibleUsers,
  TaskTrackerTaskHistoryLookups,
} from '../../../../../lib/taskTrackerTaskHistory.utils'

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message
  return fallback
}

export const useTaskTrackerTaskHistory = ({
  taskId,
  boardId,
  accessibleUsers,
  refreshToken = '',
}: {
  taskId: number | null
  boardId: number | null
  accessibleUsers: TaskTrackerAccessibleUser[]
  refreshToken?: string
}) => {
  const [events, setEvents] = useState<TaskTrackerTaskHistoryEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [columnTitlesById, setColumnTitlesById] = useState<Map<number, string>>(
    new Map(),
  )
  const [stickerTitlesById, setStickerTitlesById] = useState<
    Map<number, string>
  >(new Map())

  const loadLookups = useCallback(async () => {
    if (boardId == null) {
      setColumnTitlesById(new Map())
      setStickerTitlesById(new Map())
      return
    }

    try {
      const [boardDetails, boardStickers] = await Promise.all([
        taskTrackerApi.getBoard(boardId, { limit: 1 }),
        taskTrackerApi.getBoardStickers(boardId),
      ])

      setColumnTitlesById(
        new Map(
          boardDetails.columns.map((column) => [column.id, column.title]),
        ),
      )
      setStickerTitlesById(
        new Map(
          boardStickers.stickers.map((sticker) => [
            sticker.fieldSettingId,
            sticker.title,
          ]),
        ),
      )
    } catch {
      setColumnTitlesById(new Map())
      setStickerTitlesById(new Map())
    }
  }, [boardId])

  const loadHistory = useCallback(async () => {
    if (taskId == null) {
      setEvents([])
      setError(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const history = await taskTrackerApi.getTaskHistory(taskId)
      setEvents(history.events)
    } catch (loadError: unknown) {
      setEvents([])
      setError(getErrorMessage(loadError, 'Не удалось загрузить историю'))
    } finally {
      setLoading(false)
    }
  }, [taskId])

  useEffect(() => {
    void loadLookups()
  }, [loadLookups])

  useEffect(() => {
    void loadHistory()
  }, [loadHistory, refreshToken])

  const lookups = useMemo<TaskTrackerTaskHistoryLookups>(
    () => ({
      columnTitlesById,
      stickerTitlesById,
      performerNamesById:
        buildTaskHistoryPerformerNamesFromAccessibleUsers(accessibleUsers),
    }),
    [accessibleUsers, columnTitlesById, stickerTitlesById],
  )

  return {
    events,
    loading,
    error,
    lookups,
    reload: loadHistory,
  }
}
